import {
  collection,
  documentId,
  getCountFromServer,
  getDocs,
  limit,
  or,
  orderBy,
  query,
  startAfter,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { SUPPORT_PAGE_SIZE, supportTimestampToDate, validateSupportFilters } from '../utils/supportTickets';

export const supportFilterConstraints = (filters = {}) => {
  validateSupportFilters(filters);
  const constraints = [];

  if (filters.status && filters.status !== 'all') constraints.push(where('status', '==', filters.status));
  if (filters.startAt) {
    constraints.push(where('createdAt', '>=', Timestamp.fromDate(new Date(filters.startAt))));
    constraints.push(where('createdAt', '<', Timestamp.fromDate(new Date(filters.endAtExclusive))));
  }
  if (filters.searchTerm) {
    const term = filters.searchTerm.trim();
    const field = filters.searchField || 'ticketNumber';
    if (field === 'ticketId') constraints.push(where(documentId(), '==', term));
    else if (field === 'ticketNumber' && /^\d+$/.test(term) && Number.isSafeInteger(Number(term))) {
      // Existing rendering does not establish whether ticketNumber is stored as text or a number.
      constraints.push(or(where(field, '==', term), where(field, '==', Number(term))));
    } else constraints.push(where(field, '==', term));
  }

  return constraints;
};

const countTickets = async (constraints) => {
  const snapshot = await getCountFromServer(query(collection(db, 'supportTickets'), ...constraints));
  const count = snapshot.data().count;
  if (!Number.isSafeInteger(count) || count < 0) throw new Error('A complete support count was not returned.');
  return count;
};

export const getSupportCreatedCount = async ({ startAt, endAtExclusive }) => {
  if (!startAt || !endAtExclusive) throw new Error('A reporting range is required.');
  const [total, dated] = await Promise.all([
    countTickets([]),
    countTickets([
      where('createdAt', '>=', new Timestamp(-62135596800, 0)),
      where('createdAt', '<=', new Timestamp(253402300799, 999999999)),
    ]),
  ]);
  if (total !== dated) {
    const error = new Error('Support creation history is incomplete: some retained tickets have no valid creation timestamp.');
    error.code = 'support/incomplete-history';
    throw error;
  }
  return countTickets(supportFilterConstraints({ startAt, endAtExclusive }));
};

export const getSupportTicketsOverview = async (filters = {}) => {
  const constraints = supportFilterConstraints(filters);
  const countForStatus = (status) => filters.status && filters.status !== 'all'
    ? Promise.resolve(null)
    : countTickets([...constraints, where('status', '==', status)]);
  const [total, openCount, closedCount] = await Promise.all([
    countTickets(constraints),
    countForStatus('open'),
    countForStatus('closed'),
  ]);
  const open = filters.status === 'open' ? total : filters.status === 'closed' ? 0 : openCount;
  const closed = filters.status === 'closed' ? total : filters.status === 'open' ? 0 : closedCount;

  return {
    total,
    open,
    closed,
    // These aggregate reads have independent snapshots. Subtracting them can
    // invent an unknown-status population while tickets change status.
    other: filters.status === 'open' || filters.status === 'closed' ? 0 : null,
    // No deployed source/resolution/substantive-answer summary contract was found.
    unanswered: null,
    resolved: null,
    automaticResponses: null,
    manualResponses: null,
    manuallyResolved: null,
    countedAt: new Date().toISOString(),
  };
};

export const getSupportTicketsPage = async (filters = {}, { cursor = null, pageSize = SUPPORT_PAGE_SIZE } = {}) => {
  const size = Math.min(50, Math.max(1, Math.floor(Number(pageSize) || SUPPORT_PAGE_SIZE)));
  const constraints = supportFilterConstraints(filters);
  // ID ordering includes historical tickets with missing activity/creation timestamps.
  if (filters.startAt) constraints.push(orderBy('createdAt', 'desc'));
  constraints.push(orderBy(documentId(), 'desc'));
  if (cursor) constraints.push(startAfter(cursor));
  constraints.push(limit(size + 1));
  const snapshot = await getDocs(query(collection(db, 'supportTickets'), ...constraints));
  const visibleDocs = snapshot.docs.slice(0, size);

  return {
    tickets: visibleDocs.map((snapshotDoc) => {
      const data = snapshotDoc.data();
      return {
        ...data,
        id: snapshotDoc.id,
        createdAt: supportTimestampToDate(data.createdAt),
        lastMessageTime: supportTimestampToDate(data.lastMessageTime),
      };
    }),
    hasNext: snapshot.docs.length > size,
    nextCursor: visibleDocs[visibleDocs.length - 1] || null,
  };
};
